import React from 'react';
import StatCard, { StatGrid } from '../components/StatCard';
import ActivitySection from './ActivitySection';
import { formatRoute, formatDateTime } from '../utils/formatters';
import type { DashboardOverviewData } from '../types';
import './Dashboard.css';

interface DashboardOverviewProps {
  data: DashboardOverviewData | null;
  loading: boolean;
  lastUpdated: Date | null;
  refetch: () => void;
}

const DashboardOverview: React.FC<DashboardOverviewProps> = ({ data, loading, lastUpdated, refetch }) => {
  return (
    <div className="dashboard">
      {/* Header with refresh info */}
      <div className="dashboard-header">
        <div className="header-content">
          <h2>Общ преглед</h2>
          {lastUpdated && (
            <p className="last-updated">
              Последно актуализирано: {formatDateTime(lastUpdated)}
            </p>
          )}
        </div>
        {/* Header Refresh Button */}
        <button
          className={`floating-refresh ${loading ? 'loading' : ''}`}
          onClick={refetch}
          disabled={loading}
          title="Обновяване на данните"
        >
          <i className={`fas fa-sync-alt ${loading ? 'fa-spin' : ''}`}></i>
        </button>
      </div>

      {/* Real-time Metrics */}
      <div className="section">
        <h3 className="section-title">
          <i className="fas fa-broadcast-tower"></i>
          Активност в реално време
        </h3>
        <StatGrid>
          <StatCard
            label="Потребители онлайн"
            value={data?.users_online_now || 0}
            icon="fas fa-users"
            tone="brand"
          />
          <StatCard
            label="Активни пътувания"
            value={data?.active_rides_now || 0}
            icon="fas fa-car"
            tone="brand"
          />
        </StatGrid>
      </div>

      <ActivitySection refreshKey={lastUpdated} />

      {/* User Metrics */}
      <div className="section">
        <h3 className="section-title">
          <i className="fas fa-users"></i>
          Потребителски показатели
        </h3>
        <StatGrid>
          <StatCard
            label="Общо потребители"
            value={data?.user_metrics?.total_users?.value}
            change={data?.user_metrics?.total_users}
            icon="fas fa-user-friends"
            tone="brand"
          />
          <StatCard
            label="Влезли днес"
            value={data?.user_metrics?.active_users_daily?.value}
            change={data?.user_metrics?.active_users_daily}
            icon="fas fa-user-check"
            tone="brand"
          />
          <StatCard
            label="Нови днес"
            value={data?.user_metrics?.new_users_today?.value}
            change={data?.user_metrics?.new_users_today}
            icon="fas fa-user-plus"
            tone="brand"
          />
          <StatCard
            label="Верифицирани"
            value={data?.user_metrics?.verified_users?.value}
            change={data?.user_metrics?.verified_users}
            icon="fas fa-user-shield"
            tone="brand"
          />
        </StatGrid>
      </div>

      {/* Ride Metrics */}
      <div className="section">
        <h3 className="section-title">
          <i className="fas fa-car"></i>
          Показатели за пътувания
        </h3>
        <StatGrid>
          <StatCard
            label="Общо пътувания"
            value={data?.ride_metrics?.total_rides?.value}
            change={data?.ride_metrics?.total_rides}
            icon="fas fa-route"
            tone="success"
          />
          <StatCard
            label="Активни сега"
            value={data?.ride_metrics?.active_rides?.value}
            change={data?.ride_metrics?.active_rides}
            icon="fas fa-car-side"
            tone="success"
          />
          <StatCard
            label="Завършени"
            value={data?.ride_metrics?.completed_rides?.value}
            change={data?.ride_metrics?.completed_rides}
            icon="fas fa-check-circle"
            tone="success"
          />
          <StatCard
            label="Използване на места"
            value={data?.ride_metrics?.seat_utilization_rate || 0}
            suffix="%"
            icon="fas fa-percentage"
            tone="success"
          />
        </StatGrid>
      </div>

      {/* Booking Metrics */}
      <div className="section">
        <h3 className="section-title">
          <i className="fas fa-calendar-check"></i>
          Показатели за резервации
        </h3>
        <StatGrid>
          <StatCard
            label="Общо резервации"
            value={data?.booking_metrics?.total_bookings?.value}
            change={data?.booking_metrics?.total_bookings}
            icon="fas fa-calendar-alt"
            tone="success"
          />
          <StatCard
            label="Чакащи одобрение"
            value={data?.booking_metrics?.pending_bookings?.value}
            change={data?.booking_metrics?.pending_bookings}
            riseIsBad
            icon="fas fa-clock"
            tone="success"
          />
          <StatCard
            label="Процент одобрение"
            value={data?.booking_metrics?.booking_conversion_rate || 0}
            suffix="%"
            icon="fas fa-chart-line"
            tone="success"
          />
          <StatCard
            label="Заявки за пътуване"
            value={data?.booking_metrics?.total_ride_requests?.value}
            change={data?.booking_metrics?.total_ride_requests}
            icon="fas fa-hand-paper"
            tone="success"
          />
        </StatGrid>
      </div>

      {/* Safety Metrics */}
      <div className="section">
        <h3 className="section-title">
          <i className="fas fa-shield-alt"></i>
          Безопасност
        </h3>
        <StatGrid>
          <StatCard
            label="Спешни контакти"
            value={data?.safety_metrics?.users_with_emergency_contacts || 0}
            icon="fas fa-phone"
            tone="warning"
          />
          <StatCard
            label="Средна оценка за безопасност"
            value={data?.safety_metrics?.average_safety_score || 0}
            icon="fas fa-star"
            tone="warning"
          />
          <StatCard
            label="Верифицирани шофьори"
            value={data?.safety_metrics?.verified_drivers_percentage || 0}
            suffix="%"
            icon="fas fa-id-card"
            tone="warning"
          />
          <StatCard
            label="Инциденти днес"
            value={data?.safety_metrics?.safety_events_today || 0}
            icon="fas fa-exclamation-triangle"
            tone="error"
          />
        </StatGrid>
      </div>

      {/* Popular Routes */}
      {data?.popular_routes && data.popular_routes.length > 0 && (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-map-marked-alt"></i>
            Популярни маршрути
          </h3>
          <div className="routes-grid">
            {data.popular_routes.slice(0, 6).map((route, index) => (
              <div key={index} className="route-card">
                <div className="route-header">
                  <i className="fas fa-route"></i>
                  <span className="route-path">
                    {formatRoute(route.origin, route.destination)}
                  </span>
                </div>
                <div className="route-stats">
                  <div className="stat">
                    <span className="stat-value">{route.ride_count}</span>
                    <span className="stat-label">пътувания</span>
                  </div>
                  <div className="stat">
                    <span className="stat-value">{route.average_passengers.toFixed(1)}</span>
                    <span className="stat-label">ср. пътници</span>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Geographic Distribution */}
      {data?.cities_distribution && data.cities_distribution.length > 0 && (
        <div className="section">
          <h3 className="section-title">
            <i className="fas fa-map"></i>
            Разпределение по градове
          </h3>
          <div className="cities-grid">
            {data.cities_distribution.slice(0, 8).map((city, index) => (
              <div key={index} className="city-card">
                <div className="city-name">{city.city}</div>
                <div className="city-stats">
                  <div className="city-rides">{city.ride_count} пътувания</div>
                  <div className="city-percentage">{city.percentage}%</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Loading overlay */}
      {loading && (
        <div className="loading-overlay">
          <div className="loading-spinner">
            <i className="fas fa-spinner fa-spin"></i>
            <span>Зареждане на данните...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default DashboardOverview;
